import { useCallback } from 'react';
import { AgentProgressUIVoiceDTMF } from '@components/AgentProgressUIVoiceDTMF';
import type { Session } from '@/types/session.types';
import type { OrderInfo } from '@/types/voice-dtmf.types';
import { logger } from '@utils/logger';
import { appendQueryParams } from '@utils/appendQueryParams';
import { APP_CONFIG } from '@utils/constants';

interface VoiceManualPaymentPageProps {
  session: Session;
  /** Pre-validated payment session forwarded to useAgentApi → useAuthentication */
  initialSession?: PaymentSession;
}

export default function VoiceManualPaymentPage({
  session,
  initialSession,
}: VoiceManualPaymentPageProps) {
  const handleSessionComplete = useCallback(
    (orderInfo: OrderInfo) => {
      logger.info('Manual payment completed', {
        sessionId: session.sessionId,
        orderInfo,
      });
      const returnUrl = initialSession?.agentReturnUrl;
      if (returnUrl) {
        const fullReturnUrl = appendQueryParams(returnUrl, {
          paymentId: initialSession?.paymentId,
        });
        logger.info(
          'Redirecting agent to return URL after VOICE_MANUAL completion',
          { sessionId: session.sessionId }
        );
        window.location.href = fullReturnUrl;
      } else {
        window.parent.postMessage(
          {
            type: 'payment.completed',
            payload: {
              paymentId: initialSession?.paymentId,
              status: 'COMPLETED',
              timestamp: new Date().toISOString(),
            },
          },
          '*'
        );
      }
    },
    [
      initialSession?.agentReturnUrl,
      initialSession?.paymentId,
      session.sessionId,
    ]
  );

  const handleSessionDeclined = useCallback(
    (_pid?: string) => {
      logger.info('Manual payment declined', {
        sessionId: session.sessionId,
      });
      const returnUrl = initialSession?.agentReturnUrl;
      if (returnUrl) {
        window.location.href = appendQueryParams(returnUrl, {
          paymentId: initialSession?.paymentId,
        });
      } else {
        window.parent.postMessage(
          {
            eventName: 'integration-failure',
            type: 'payment.completed',
            payload: {
              paymentId: initialSession?.paymentId,
              status: 'DECLINED',
              timestamp: new Date().toISOString(),
            },
          },
          '*'
        );
      }
    },
    [
      initialSession?.agentReturnUrl,
      initialSession?.paymentId,
      session.sessionId,
    ]
  );

  const handleSessionCancel = useCallback(() => {
    logger.info('Manual payment cancelled', { sessionId: session.sessionId });
  }, [session.sessionId]);

  const handleError = useCallback(
    (error: Error) => {
      logger.error('Manual payment error', error, {
        sessionId: session.sessionId,
      });
    },
    [session.sessionId]
  );

  return (
    <AgentProgressUIVoiceDTMF
      sessionId={session.sessionId}
      customerId={session.customerId}
      amount={session.amount}
      currency={session.currency}
      useMockData={APP_CONFIG.USE_MOCK_DATA}
      mockScenario={
        APP_CONFIG.USE_MOCK_DATA ? 'manual-mode-success' : undefined
      }
      onSessionComplete={handleSessionComplete}
      onSessionDeclined={handleSessionDeclined}
      onSessionCancel={handleSessionCancel}
      onError={handleError}
      initialSession={initialSession}
      initialManualModeEnabled={initialSession?.manualModeEnabled}
      callReference={initialSession?.callReference}
      cancelButtonEnabled={initialSession?.cancelButtonEnabled ?? false}
      interactionType="VOICE_MANUAL"
    />
  );
}
